
import { onClickOutside } from '@vueuse/core';
import {
  computed,
  defineComponent,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { useDriveStore } from '../modules/drive/store/driveStore';
import {
  ISaveCasePayload, ISearchLabelInput, IFileUpload, ICaseFilesManage, IError, ICase,
} from '@/types/interfaces';
import DriveInput from '../modules/drive/components/UI/DriveInput.vue';
import { useMainStore } from '@/store/mainStore';
import UseUpload from '@/composables/UseUpload';
import UploadService from '@/services/UploadService';
import AppLoader from './UI/AppLoader.vue';
import AppUploadInfo from '@/components/UI/AppUploadInfo.vue';
import { UPLOAD_PLACES } from '@/types/enums';
import AppSkeletonCaseEdit from './skeletons/AppSkeletonCaseEdit.vue';
import NotificationService from '@/services/NotificationService';

export default defineComponent({
  components: {
    DriveInput,
    // AppProgress,
    AppLoader,
    AppUploadInfo,
    AppSkeletonCaseEdit,
  },
  props: {
    fileToUpload: { type: Array as () => IFileUpload[], default: [] as IFileUpload[] },
    urlToUpload: { type: String, required: false },
    labels: { type: Array as () => ISearchLabelInput[], default: [] as ISearchLabelInput[] },
  },
  setup(props) {
    const modal = ref();
    const isShow = ref(false);

    const router = useRouter();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();

    const isProgress = ref(false);
    const isUploading = ref(false);
    const percent = ref(0);
    const caseTitle = ref('');
    const error = ref('');

    const disabled = computed(() => !!error.value || !caseTitle.value);

    const { onUploadUpdate, uploadInfo } = UseUpload();

    const loaderText = computed(() => '');

    function close() {
      if (isProgress.value || isUploading.value) {
        return;
      }
      isShow.value = false;
    }
    onClickOutside(modal, close);

    function onUpdate(per: number) { percent.value = per; }

    async function createCaseFromPhotoFile(): Promise<void> {
      if (disabled.value || !props.fileToUpload) return;

      isProgress.value = true;
      const payload: ISaveCasePayload = {
        title: caseTitle.value,
        folderId: mainStore.userSettings.accountSystemSettings.userCasesFolderId,
      };

      try {
        const caseResponse = await driveStore.createCase(payload, onUpdate);

        UploadService.batchUploadTUS(props.fileToUpload, onUploadUpdate, { location: UPLOAD_PLACES.CASE_PHOTO, id: caseResponse.id! }, undefined, props.labels, true)
          .then((media) => {
            const filesPayload = {
              id: caseResponse.id,
              fileIds: media.map((item) => item.id),
            } as ICaseFilesManage;

            driveStore.addFilesToCase(filesPayload);
          })
          .finally(() => {
            isProgress.value = false;
            percent.value = 0;
          });

        router.push(`/drive/case/${caseResponse.id}/edit`).then(() => {
          isProgress.value = false;
          percent.value = 0;
          driveStore.fullscreenPhoto = undefined;
          driveStore.fullscreenFile = undefined;
          mainStore.BottomPopUpType = null;
          close();
        });
      } catch (e: any) {
        if (e?.response?.data?.errorKey === 'CASE_NAME_EXISTS') {
          error.value = 'Name is already taken';
        } else {
          error.value = 'Something gone wrong';
        }
        isProgress.value = false;
        percent.value = 0;
      }
    }

    function createCaseFromPhotoUrl(): void {
      if (disabled.value || !props.urlToUpload) return;

      isProgress.value = true;
      const payload: ISaveCasePayload = {
        title: caseTitle.value,
        folderId: mainStore.userSettings.accountSystemSettings.userCasesFolderId,
      };

      driveStore.createCase(payload, onUpdate)
        .then((iCase) => {
          const filesPayload = {
            id: iCase.id,
            fileIds: [props.urlToUpload],
          } as ICaseFilesManage;

          driveStore.addFilesToCase(filesPayload).then(() => {
            // NotificationService.notifySuccess(`Case ${caseTitle.value} has been created.`);
          });

          router.push(`/drive/case/${(iCase.id)}/edit`).then(() => {
            mainStore.BottomPopUpType = null;
            driveStore.fullscreenPhoto = undefined;
            driveStore.fullscreenFile = undefined;
            driveStore.appPhotoViewMedia = undefined;
            mainStore.BottomPopUpType = null;
          });
        })
        .catch(() => {
          error.value = 'Name is already taken';
          isProgress.value = false;
          percent.value = 0;
        });
    }

    function handleCreateCase() {
      if (props.fileToUpload.length) {
        createCaseFromPhotoFile();
      } else if (props.urlToUpload) {
        createCaseFromPhotoUrl();
      }
    }

    function cancelUpload() {
      UploadService.isAbortUpload = true;
      isProgress.value = false;
      isUploading.value = false;
      percent.value = 0;
    }

    watch(() => caseTitle.value, () => {
      if (!caseTitle.value) {
        error.value = 'This filed is required';
      } else {
        error.value = '';
      }
    });

    return {
      isShow,
      modal,
      caseTitle,
      isProgress,
      isUploading,
      loaderText,
      percent,
      error,
      disabled,
      uploadInfo,
      close,
      createCaseFromPhotoFile,
      handleCreateCase,
      cancelUpload,
      // mainStore,
    };
  },
});
